import React from 'react';
import { ArrowLeft, MapPin, AlertTriangle } from 'lucide-react';
import type { VergeCondition } from './types';

interface WallOfShameProps {
  reports: VergeCondition[];
  onBack: () => void;
}

interface AuthoritySummary {
  name: string;
  count: number;
  worstRoads: {
    name: string;
    count: number;
    grade: 'C' | 'D';
  }[];
}

export function WallOfShame({ reports, onBack }: WallOfShameProps) {
  // Filter for only Grade C and D reports, excluding unknown authorities
  const badReports = reports.filter(report => 
    (report.grade === 'C' || report.grade === 'D') && 
    report.local_authority && 
    report.local_authority !== 'Unknown Authority' &&
    report.local_authority !== 'Unknown authority'
  );

  // Group by local authority
  const authorityStats = badReports.reduce<Record<string, AuthoritySummary>>((acc, report) => {
    const authority = report.local_authority!; // We know it's not null from the filter
    const road = report.street_name || 'Unknown Road';

    if (!acc[authority]) {
      acc[authority] = {
        name: authority,
        count: 0,
        worstRoads: []
      };
    }

    acc[authority].count++;

    // Update worst roads for this authority
    const existingRoad = acc[authority].worstRoads.find(r => r.name === road);
    if (existingRoad) {
      existingRoad.count++;
      if (report.grade === 'D') existingRoad.grade = 'D';
    } else {
      acc[authority].worstRoads.push({
        name: road,
        count: 1,
        grade: report.grade as 'C' | 'D'
      });
    }

    return acc;
  }, {});

  // Sort authorities by count
  const sortedAuthorities = Object.values(authorityStats)
    .sort((a, b) => b.count - a.count)
    .map(authority => ({
      ...authority,
      worstRoads: authority.worstRoads
        .sort((a, b) => b.count - a.count)
        .slice(0, 3) // Top 3 worst roads
    }));

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <header className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <AlertTriangle className="w-6 h-6 mr-2 text-red-500" />
            Wall of Shame
          </h1>
          <div className="w-10" /> {/* Spacer for centering */}
        </header>

        <div className="space-y-6">
          {sortedAuthorities.map((authority) => (
            <div 
              key={authority.name}
              className="bg-gray-50 rounded-lg shadow-lg p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-semibold text-gray-800">{authority.name}</h2>
                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                  {authority.count} poor condition{authority.count === 1 ? '' : 's'}
                </span>
              </div>
              
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-600">Worst affected roads:</h3>
                {authority.worstRoads.map((road) => (
                  <div 
                    key={road.name}
                    className="flex items-center justify-between bg-white rounded-lg p-3"
                  >
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-800">{road.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`${road.grade === 'D' ? 'bg-red-500' : 'bg-orange-500'} text-white px-2 py-0.5 rounded text-sm`}>
                        Grade {road.grade}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({road.count} report{road.count === 1 ? '' : 's'})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {sortedAuthorities.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No Grade C or D reports found.</p>
              <p className="text-sm mt-1">That's good news!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}